import supabase from '../lib/supabase';
import {appointmentService} from './appointmentService';

export const bookingService = {
  // Test connection and initialize table if needed
  async testConnection() {
    try {
      // First check if table exists
      const {data: tableExists, error: tableError} = await supabase
        .from('bookings_patient_data_bp2025')
        .select('id')
        .limit(1);

      if (tableError && tableError.code === 'PGRST116') {
        // Table doesn't exist, let's create it
        const createResult = await this.createTableIfNotExists();
        if (!createResult.success) {
          return createResult;
        }
      } else if (tableError) {
        return {success: false, error: tableError};
      }

      // Ensure all required columns exist
      await this.ensureAllColumns();
      return {success: true, data: tableExists};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Ensure all required columns exist in the table
  async ensureAllColumns() {
    try {
      // First, let's run the SQL to create missing columns using a direct approach
      const sqlCommands = [
        // Add cancellation_reason column if it doesn't exist
        `ALTER TABLE bookings_patient_data_bp2025 ADD COLUMN IF NOT EXISTS cancellation_reason TEXT;`,
        
        // Add cancelled_at column if it doesn't exist
        `ALTER TABLE bookings_patient_data_bp2025 ADD COLUMN IF NOT EXISTS cancelled_at TIMESTAMP WITH TIME ZONE;`,
        
        // Add reschedule_count column if it doesn't exist
        `ALTER TABLE bookings_patient_data_bp2025 ADD COLUMN IF NOT EXISTS reschedule_count INTEGER DEFAULT 0;`,
        
        // Add original_appointment_date column if it doesn't exist
        `ALTER TABLE bookings_patient_data_bp2025 ADD COLUMN IF NOT EXISTS original_appointment_date DATE;`,
        
        // Add original_appointment_time column if it doesn't exist
        `ALTER TABLE bookings_patient_data_bp2025 ADD COLUMN IF NOT EXISTS original_appointment_time TIME;`,
        
        // Add last_rescheduled_at column if it doesn't exist
        `ALTER TABLE bookings_patient_data_bp2025 ADD COLUMN IF NOT EXISTS last_rescheduled_at TIMESTAMP WITH TIME ZONE;`,
        
        // Create indexes if they don't exist
        `CREATE INDEX IF NOT EXISTS idx_bookings_cancelled_at ON bookings_patient_data_bp2025(cancelled_at);`,
        `CREATE INDEX IF NOT EXISTS idx_bookings_status ON bookings_patient_data_bp2025(status);`,
        `CREATE INDEX IF NOT EXISTS idx_bookings_reschedule ON bookings_patient_data_bp2025(reschedule_count);`,
        `CREATE INDEX IF NOT EXISTS idx_bookings_last_rescheduled ON bookings_patient_data_bp2025(last_rescheduled_at);`
      ];

      // Execute each command individually
      for (const sql of sqlCommands) {
        try {
          await supabase.rpc('exec_sql', { sql });
        } catch (error) {
          console.warn('Failed to execute SQL:', sql, error);
        }
      }

      return {success: true};
    } catch (error) {
      console.warn('Error ensuring columns:', error);
      return {success: true}; // Continue without additional columns
    }
  },

  // Check what columns are available in the table
  async getAvailableColumns() {
    try {
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select('*')
        .limit(1);

      if (error) {
        return {success: false, error};
      }

      const availableColumns = data && data.length > 0 ? Object.keys(data[0]) : [];
      return {success: true, columns: availableColumns};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get dynamic select query based on available columns
  async getDynamicSelectQuery() {
    const baseColumns = `
      id, booking_id, patient_name, patient_email, patient_phone, patient_gender, 
      patient_age, patient_place, wheelchair_assistance, appointment_type_name, 
      appointment_type_id, appointment_date, appointment_time, consultation_fee, 
      location, google_maps_url, duration, additional_info, status, created_at, updated_at
    `;

    try {
      // Test if additional columns exist by trying to select them
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select('cancellation_reason, cancelled_at, reschedule_count, original_appointment_date, original_appointment_time, last_rescheduled_at')
        .limit(1);

      if (!error) {
        return baseColumns + ', cancellation_reason, cancelled_at, reschedule_count, original_appointment_date, original_appointment_time, last_rescheduled_at';
      }
    } catch (e) {
      // Additional columns don't exist, use base columns only
    }

    return baseColumns;
  },

  // Create table if it doesn't exist
  async createTableIfNotExists() {
    try {
      // Create table with all required columns from the start
      const createTableSQL = `
        CREATE TABLE IF NOT EXISTS bookings_patient_data_bp2025 (
          id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
          booking_id TEXT NOT NULL,
          patient_name TEXT NOT NULL,
          patient_email TEXT,
          patient_phone TEXT,
          patient_gender TEXT,
          patient_age INTEGER,
          patient_place TEXT,
          wheelchair_assistance BOOLEAN DEFAULT FALSE,
          appointment_type_name TEXT,
          appointment_type_id UUID,
          appointment_date DATE NOT NULL,
          appointment_time TIME,
          consultation_fee DECIMAL(10,2),
          location TEXT,
          google_maps_url TEXT,
          duration INTEGER DEFAULT 30,
          additional_info TEXT,
          status TEXT DEFAULT 'pending',
          cancellation_reason TEXT,
          cancelled_at TIMESTAMP WITH TIME ZONE,
          reschedule_count INTEGER DEFAULT 0,
          original_appointment_date DATE,
          original_appointment_time TIME,
          last_rescheduled_at TIMESTAMP WITH TIME ZONE,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );

        -- Enable RLS
        ALTER TABLE bookings_patient_data_bp2025 ENABLE ROW LEVEL SECURITY;

        -- Create policies
        DROP POLICY IF EXISTS "Allow all operations on bookings_patient_data_bp2025" ON bookings_patient_data_bp2025;
        CREATE POLICY "Allow all operations on bookings_patient_data_bp2025" 
        ON bookings_patient_data_bp2025 FOR ALL USING (true) WITH CHECK (true);

        -- Create indexes
        CREATE INDEX IF NOT EXISTS idx_bookings_appointment_date ON bookings_patient_data_bp2025(appointment_date);
        CREATE INDEX IF NOT EXISTS idx_bookings_status ON bookings_patient_data_bp2025(status);
        CREATE INDEX IF NOT EXISTS idx_bookings_cancelled_at ON bookings_patient_data_bp2025(cancelled_at);
        CREATE INDEX IF NOT EXISTS idx_bookings_created_at ON bookings_patient_data_bp2025(created_at);
        CREATE INDEX IF NOT EXISTS idx_bookings_reschedule ON bookings_patient_data_bp2025(reschedule_count);
        CREATE INDEX IF NOT EXISTS idx_bookings_last_rescheduled ON bookings_patient_data_bp2025(last_rescheduled_at);
      `;

      const {data, error} = await supabase.rpc('exec_sql', { sql: createTableSQL });

      if (error) {
        return {success: false, error};
      }

      return {success: true, data};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Initialize database with proper schema
  async initializeDatabase() {
    try {
      console.log('Initializing database schema...');
      
      // First ensure the table exists with all columns
      const createResult = await this.createTableIfNotExists();
      if (!createResult.success) {
        console.error('Failed to create table:', createResult.error);
      }

      // Then ensure all columns exist
      const columnsResult = await this.ensureAllColumns();
      if (!columnsResult.success) {
        console.error('Failed to ensure columns:', columnsResult.error);
      }

      // Test the final structure
      const testResult = await this.testConnection();
      
      return testResult;
    } catch (error) {
      console.error('Database initialization error:', error);
      return {success: false, error: error.message};
    }
  },

  // Check if table exists and has correct structure
  async checkTableStructure() {
    try {
      const selectQuery = await this.getDynamicSelectQuery();
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .limit(1);

      if (error) {
        return {success: false, error};
      }

      return {success: true, message: 'Structure is correct'};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get only enabled appointment types for booking
  async getAvailableAppointmentTypes() {
    try {
      const result = await appointmentService.getEnabledAppointmentTypes();
      return result;
    } catch (error) {
      console.error('Error getting available appointment types:', error);
      return {success: false, error: error.message};
    }
  },

  // Check if appointment type is enabled before booking
  async validateAppointmentType(appointmentTypeId) {
    try {
      const {data, error} = await supabase
        .from('appointment_types_bp2025')
        .select('id, name, is_enabled, consultation_fee, duration, time_slots, available_days')
        .eq('id', appointmentTypeId)
        .eq('is_enabled', true)
        .single();

      if (error) {
        return {success: false, error: 'Appointment type not found or not available'};
      }

      if (!data) {
        return {success: false, error: 'This appointment type is currently not available for booking'};
      }

      return {success: true, data};
    } catch (error) {
      return {success: false, error: error.message};
    }
  },

  // NEW: Get available time slots for rescheduling
  async getAvailableTimeSlotsForReschedule(appointmentTypeId, selectedDate, excludeAppointmentId = null) {
    try {
      // Get appointment type details including time slots
      const appointmentTypeResult = await this.validateAppointmentType(appointmentTypeId);
      if (!appointmentTypeResult.success) {
        return appointmentTypeResult;
      }

      const appointmentType = appointmentTypeResult.data;
      const availableSlots = appointmentType.time_slots || [];

      if (availableSlots.length === 0) {
        return {
          success: true,
          data: {
            availableSlots: [],
            appointmentType,
            message: 'No time slots configured for this appointment type'
          }
        };
      }

      // Get existing bookings for the selected date (excluding current appointment if rescheduling)
      let query = supabase
        .from('bookings_patient_data_bp2025')
        .select('appointment_time, status')
        .eq('appointment_date', selectedDate)
        .eq('appointment_type_id', appointmentTypeId)
        .in('status', ['confirmed', 'pending']); // Don't count cancelled appointments

      if (excludeAppointmentId) {
        query = query.neq('id', excludeAppointmentId);
      }

      const {data: existingBookings, error} = await query;

      if (error) {
        return {success: false, error: error.message};
      }

      // Get booked time slots
      const bookedSlots = new Set(existingBookings.map(booking => booking.appointment_time));

      // Filter available slots
      const availableTimeSlots = availableSlots.filter(slot => {
        // Extract start time from slot (format: "09:00-09:30")
        const startTime = slot.split('-')[0];
        return !bookedSlots.has(startTime);
      });

      return {
        success: true,
        data: {
          availableSlots: availableTimeSlots,
          appointmentType,
          totalSlots: availableSlots.length,
          bookedSlots: existingBookings.length,
          selectedDate
        }
      };
    } catch (error) {
      return {success: false, error: error.message};
    }
  },

  // NEW: Reschedule appointment with improved column handling
  async rescheduleAppointment(appointmentId, newDate, newTimeSlot, rescheduleReason = null) {
    try {
      console.log('Rescheduling appointment:', {appointmentId, newDate, newTimeSlot, rescheduleReason});

      // First, get the current appointment details using dynamic query
      const selectQuery = await this.getDynamicSelectQuery();
      const {data: currentAppointment, error: fetchError} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .eq('id', appointmentId)
        .single();

      if (fetchError) {
        return {success: false, error: fetchError.message};
      }

      if (!currentAppointment) {
        return {success: false, error: 'Appointment not found'};
      }

      // Check if appointment can be rescheduled
      if (currentAppointment.status === 'cancelled') {
        return {success: false, error: 'Cannot reschedule a cancelled appointment'};
      }

      if (currentAppointment.status === 'completed') {
        return {success: false, error: 'Cannot reschedule a completed appointment'};
      }

      // Extract start time from time slot (format: "09:00-09:30")
      const newTime = newTimeSlot.split('-')[0];

      // Check if the new time slot is available
      const availabilityCheck = await this.checkTimeSlotAvailability(
        currentAppointment.appointment_type_id,
        newDate,
        newTime
      );

      if (!availabilityCheck.success) {
        return availabilityCheck;
      }

      if (!availabilityCheck.available) {
        return {success: false, error: 'Selected time slot is no longer available. Please choose a different time.'};
      }

      // Prepare reschedule data with only basic required fields
      const rescheduleData = {
        appointment_date: newDate,
        appointment_time: newTime,
        updated_at: new Date().toISOString(),
        status: 'confirmed' // Ensure rescheduled appointment is confirmed
      };

      // Check if reschedule tracking columns exist and add them
      try {
        const columnsCheck = await supabase
          .from('bookings_patient_data_bp2025')
          .select('reschedule_count, original_appointment_date, original_appointment_time, last_rescheduled_at')
          .limit(1);

        if (!columnsCheck.error) {
          // Columns exist, add reschedule tracking
          rescheduleData.reschedule_count = (currentAppointment.reschedule_count || 0) + 1;
          rescheduleData.last_rescheduled_at = new Date().toISOString();

          // Set original appointment details if this is the first reschedule
          if (!currentAppointment.original_appointment_date) {
            rescheduleData.original_appointment_date = currentAppointment.appointment_date;
            rescheduleData.original_appointment_time = currentAppointment.appointment_time;
          }
        }
      } catch (e) {
        // Reschedule columns don't exist, continue without them
        console.log('Reschedule tracking columns not available');
      }

      // Add reschedule reason if provided (store in additional_info)
      if (rescheduleReason) {
        const currentInfo = currentAppointment.additional_info || '';
        const rescheduleInfo = `[Reschedule ${(currentAppointment.reschedule_count || 0) + 1}] ${rescheduleReason}`;
        rescheduleData.additional_info = currentInfo ? `${currentInfo}\n${rescheduleInfo}` : rescheduleInfo;
      }

      // Update the appointment
      const {data: updatedAppointment, error: updateError} = await supabase
        .from('bookings_patient_data_bp2025')
        .update(rescheduleData)
        .eq('id', appointmentId)
        .select()
        .single();

      if (updateError) {
        return {success: false, error: updateError.message};
      }

      return {
        success: true,
        data: updatedAppointment,
        message: `Appointment successfully rescheduled to ${newDate} at ${this.formatTimeSlot(newTimeSlot)}`
      };
    } catch (error) {
      return {success: false, error: error.message};
    }
  },

  // Helper: Format time slot for display
  formatTimeSlot(timeSlot) {
    try {
      const [startTime, endTime] = timeSlot.split('-');
      const formatTime = (time) => {
        const [hours, minutes] = time.split(':');
        const hour24 = parseInt(hours, 10);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      };
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    } catch (error) {
      return timeSlot;
    }
  },

  // NEW: Get reschedule suggestions based on appointment type and preferences
  async getReschedulesSuggestions(appointmentId, daysAhead = 7) {
    try {
      // Get current appointment details using dynamic query
      const selectQuery = await this.getDynamicSelectQuery();
      const {data: appointment, error: fetchError} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .eq('id', appointmentId)
        .single();

      if (fetchError || !appointment) {
        return {success: false, error: 'Appointment not found'};
      }

      const suggestions = [];
      const today = new Date();
      const currentDate = new Date(appointment.appointment_date);

      // Start suggestions from tomorrow or current appointment date, whichever is later
      const startDate = new Date(Math.max(today.getTime() + 24 * 60 * 60 * 1000, currentDate.getTime()));

      // Get appointment type details
      const appointmentTypeResult = await this.validateAppointmentType(appointment.appointment_type_id);
      if (!appointmentTypeResult.success) {
        return appointmentTypeResult;
      }

      const appointmentType = appointmentTypeResult.data;
      const availableDays = appointmentType.available_days || [];

      // Generate suggestions for the next daysAhead days
      for (let i = 0; i < daysAhead; i++) {
        const checkDate = new Date(startDate);
        checkDate.setDate(startDate.getDate() + i);
        
        const dayName = checkDate.toLocaleDateString('en-US', {weekday: 'long'});
        const dateString = checkDate.toISOString().split('T')[0];

        // Check if this day is available for this appointment type
        if (availableDays.includes(dayName)) {
          const slotsResult = await this.getAvailableTimeSlotsForReschedule(
            appointment.appointment_type_id,
            dateString,
            appointmentId
          );

          if (slotsResult.success && slotsResult.data.availableSlots.length > 0) {
            suggestions.push({
              date: dateString,
              dayName,
              displayDate: checkDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              }),
              availableSlots: slotsResult.data.availableSlots,
              totalSlots: slotsResult.data.availableSlots.length
            });
          }
        }
      }

      return {
        success: true,
        data: {
          suggestions,
          appointmentType,
          currentAppointment: appointment,
          message: suggestions.length > 0 
            ? `Found ${suggestions.length} days with available slots` 
            : 'No available slots found in the next week'
        }
      };
    } catch (error) {
      return {success: false, error: error.message};
    }
  },

  // Get ALL appointments (unlimited) - UPDATED METHOD with dynamic column selection
  async getAllAppointments() {
    try {
      // Initialize database first to ensure all columns exist
      await this.initializeDatabase();
      
      const selectQuery = await this.getDynamicSelectQuery();
      
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .order('appointment_date', {ascending: false})
        .order('appointment_time', {ascending: false});

      if (error) {
        return {success: false, error};
      }

      return {success: true, data: data || []};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get latest 10 appointments with detailed information (keeping for backward compatibility)
  async getLatestAppointments() {
    try {
      const selectQuery = await this.getDynamicSelectQuery();
      
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .order('created_at', {ascending: false})
        .limit(10);

      if (error) {
        return {success: false, error};
      }

      return {success: true, data: data || []};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get appointments by date range - UPDATED METHOD
  async getAppointmentsByDateRange(startDate, endDate) {
    try {
      const selectQuery = await this.getDynamicSelectQuery();
      
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .gte('appointment_date', startDate)
        .lte('appointment_date', endDate)
        .order('appointment_date', {ascending: true})
        .order('appointment_time', {ascending: true});

      if (error) {
        return {success: false, error};
      }

      return {success: true, data: data || []};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get appointments by status - UPDATED METHOD
  async getAppointmentsByStatus(status) {
    try {
      const selectQuery = await this.getDynamicSelectQuery();
      
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .eq('status', status)
        .order('appointment_date', {ascending: false})
        .order('appointment_time', {ascending: false});

      if (error) {
        return {success: false, error};
      }

      return {success: true, data: data || []};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get appointments by appointment type - UPDATED METHOD
  async getAppointmentsByType(appointmentType) {
    try {
      const selectQuery = await this.getDynamicSelectQuery();
      
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .ilike('appointment_type_name', `%${appointmentType}%`)
        .order('appointment_date', {ascending: false})
        .order('appointment_time', {ascending: false});

      if (error) {
        return {success: false, error};
      }

      return {success: true, data: data || []};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get today's appointments
  async getTodayAppointments() {
    try {
      const today = new Date();
      const todayString = today.toISOString().split('T')[0];
      const selectQuery = await this.getDynamicSelectQuery();
      
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .eq('appointment_date', todayString)
        .order('appointment_time', {ascending: true});

      if (error) {
        return {success: false, error};
      }

      return {success: true, data: data || []};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Get appointment statistics
  async getAppointmentStats() {
    try {
      const today = new Date();
      const todayString = today.toISOString().split('T')[0];

      // Get all counts in parallel
      const [totalResult, todayResult, pendingResult, completedResult] = await Promise.all([
        supabase
          .from('bookings_patient_data_bp2025')
          .select('*', {count: 'exact', head: true}),
        supabase
          .from('bookings_patient_data_bp2025')
          .select('*', {count: 'exact', head: true})
          .eq('appointment_date', todayString),
        supabase
          .from('bookings_patient_data_bp2025')
          .select('*', {count: 'exact', head: true})
          .eq('status', 'pending'),
        supabase
          .from('bookings_patient_data_bp2025')
          .select('*', {count: 'exact', head: true})
          .eq('appointment_date', todayString)
          .eq('status', 'completed')
      ]);

      const stats = {
        totalAppointments: totalResult.count || 0,
        todayAppointments: todayResult.count || 0,
        pendingAppointments: pendingResult.count || 0,
        completedAppointments: completedResult.count || 0
      };

      return {success: true, data: stats};
    } catch (error) {
      return {
        success: true,
        data: {
          totalAppointments: 0,
          todayAppointments: 0,
          pendingAppointments: 0,
          completedAppointments: 0
        }
      };
    }
  },

  // Create a new booking with validation
  async createBooking(bookingData) {
    try {
      // First validate that the appointment type is enabled
      if (bookingData.appointment_type_id) {
        const validationResult = await this.validateAppointmentType(bookingData.appointment_type_id);
        if (!validationResult.success) {
          return validationResult;
        }
      }

      const newBooking = {
        booking_id: bookingData.booking_id || `BK${Date.now()}`,
        patient_name: bookingData.patient_name,
        patient_email: bookingData.patient_email,
        patient_phone: bookingData.patient_phone,
        patient_gender: bookingData.patient_gender || 'Not specified',
        patient_age: bookingData.patient_age || null,
        patient_place: bookingData.patient_place || 'Not specified',
        wheelchair_assistance: bookingData.wheelchair_assistance || false,
        appointment_type_name: bookingData.appointment_type_name || 'General Consultation',
        appointment_type_id: bookingData.appointment_type_id || null,
        appointment_date: bookingData.appointment_date,
        appointment_time: bookingData.appointment_time,
        consultation_fee: bookingData.consultation_fee || 0,
        location: bookingData.location || 'Best Podiatrist Clinic',
        google_maps_url: bookingData.google_maps_url || null,
        duration: bookingData.duration || 30,
        additional_info: bookingData.additional_info || null,
        status: bookingData.status || 'confirmed',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Only add reschedule_count if the column exists
      try {
        const testQuery = await supabase
          .from('bookings_patient_data_bp2025')
          .select('reschedule_count')
          .limit(1);
        
        if (!testQuery.error) {
          newBooking.reschedule_count = 0;
        }
      } catch (e) {
        // Column doesn't exist, continue without it
      }

      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .insert([newBooking])
        .select()
        .single();

      if (error) {
        return {success: false, error};
      }

      return {success: true, data};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Update booking status
  async updateBookingStatus(bookingId, status) {
    try {
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId)
        .select()
        .single();

      if (error) {
        return {success: false, error};
      }

      return {success: true, data};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Cancel appointment and make time slot available - with improved column handling
  async cancelAppointment(appointmentId, cancellationReason = null) {
    try {
      console.log('Cancelling appointment:', appointmentId);

      // First, get the appointment details using dynamic query
      const selectQuery = await this.getDynamicSelectQuery();
      const {data: appointment, error: fetchError} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .eq('id', appointmentId)
        .single();

      if (fetchError) {
        console.error('Error fetching appointment:', fetchError);
        return {success: false, error: fetchError.message};
      }

      if (!appointment) {
        return {success: false, error: 'Appointment not found'};
      }

      // Check if appointment is already cancelled
      if (appointment.status === 'cancelled') {
        return {success: false, error: 'Appointment is already cancelled'};
      }

      // Prepare update data - start with basic required fields
      const updateData = {
        status: 'cancelled',
        updated_at: new Date().toISOString()
      };

      // Only add cancellation fields if columns exist
      try {
        const testResult = await supabase
          .from('bookings_patient_data_bp2025')
          .select('cancellation_reason, cancelled_at')
          .limit(1);

        if (!testResult.error) {
          updateData.cancellation_reason = cancellationReason;
          updateData.cancelled_at = new Date().toISOString();
        }
      } catch (e) {
        // Columns don't exist, continue without them
        console.log('Cancellation columns not available, updating status only');
      }

      // Update appointment status to cancelled
      const {data: updatedAppointment, error: updateError} = await supabase
        .from('bookings_patient_data_bp2025')
        .update(updateData)
        .eq('id', appointmentId)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating appointment:', updateError);
        return {success: false, error: updateError.message};
      }

      console.log('Successfully cancelled appointment:', updatedAppointment);

      // The time slot becomes automatically available since we're not tracking
      // booked slots separately - when someone books, we just check existing appointments
      // This means cancelled appointments automatically free up their time slots
      return {
        success: true,
        data: updatedAppointment,
        message: 'Appointment cancelled successfully. Time slot is now available for new bookings.'
      };
    } catch (error) {
      console.error('Error in cancelAppointment:', error);
      return {success: false, error: error.message};
    }
  },

  // Get appointment by ID
  async getAppointmentById(appointmentId) {
    try {
      const selectQuery = await this.getDynamicSelectQuery();
      
      const {data, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .eq('id', appointmentId)
        .single();

      if (error) {
        return {success: false, error};
      }

      return {success: true, data};
    } catch (error) {
      return {success: false, error};
    }
  },

  // Reactivate cancelled appointment (if time slot is still available)
  async reactivateAppointment(appointmentId) {
    try {
      console.log('Reactivating appointment:', appointmentId);

      // Get the appointment details using dynamic query
      const selectQuery = await this.getDynamicSelectQuery();
      const {data: appointment, error: fetchError} = await supabase
        .from('bookings_patient_data_bp2025')
        .select(selectQuery)
        .eq('id', appointmentId)
        .single();

      if (fetchError) {
        return {success: false, error: fetchError.message};
      }

      if (!appointment) {
        return {success: false, error: 'Appointment not found'};
      }

      if (appointment.status !== 'cancelled') {
        return {success: false, error: 'Only cancelled appointments can be reactivated'};
      }

      // Check if the time slot is still available (no other confirmed appointments at same time)
      const {data: conflictingAppointments, error: conflictError} = await supabase
        .from('bookings_patient_data_bp2025')
        .select('id')
        .eq('appointment_date', appointment.appointment_date)
        .eq('appointment_time', appointment.appointment_time)
        .eq('appointment_type_id', appointment.appointment_type_id)
        .in('status', ['confirmed', 'pending'])
        .neq('id', appointmentId);

      if (conflictError) {
        return {success: false, error: conflictError.message};
      }

      if (conflictingAppointments && conflictingAppointments.length > 0) {
        return {success: false, error: 'Time slot is no longer available - another appointment has been booked'};
      }

      // Prepare reactivation data
      const updateData = {
        status: 'confirmed',
        updated_at: new Date().toISOString()
      };

      // Try to clear cancellation fields if columns exist
      try {
        const testResult = await supabase
          .from('bookings_patient_data_bp2025')
          .select('cancellation_reason, cancelled_at')
          .limit(1);

        if (!testResult.error) {
          updateData.cancellation_reason = null;
          updateData.cancelled_at = null;
        }
      } catch (e) {
        // Columns don't exist, continue without them
      }

      // Reactivate the appointment
      const {data: updatedAppointment, error: updateError} = await supabase
        .from('bookings_patient_data_bp2025')
        .update(updateData)
        .eq('id', appointmentId)
        .select()
        .single();

      if (updateError) {
        return {success: false, error: updateError.message};
      }

      return {
        success: true,
        data: updatedAppointment,
        message: 'Appointment reactivated successfully'
      };
    } catch (error) {
      return {success: false, error: error.message};
    }
  },

  // Check if time slot is available for booking
  async checkTimeSlotAvailability(appointmentTypeId, appointmentDate, appointmentTime) {
    try {
      const {data: existingAppointments, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select('id, status')
        .eq('appointment_type_id', appointmentTypeId)
        .eq('appointment_date', appointmentDate)
        .eq('appointment_time', appointmentTime)
        .in('status', ['confirmed', 'pending']); // Don't count cancelled appointments

      if (error) {
        return {success: false, error: error.message};
      }

      const isAvailable = !existingAppointments || existingAppointments.length === 0;

      return {
        success: true,
        available: isAvailable,
        conflictingAppointments: existingAppointments || []
      };
    } catch (error) {
      return {success: false, error: error.message};
    }
  },

  // Subscribe to real-time changes
  subscribeToChanges(callback) {
    try {
      const subscription = supabase
        .channel('bookings_patient_data_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bookings_patient_data_bp2025'
          },
          (payload) => {
            callback(payload);
          }
        )
        .subscribe();

      return subscription;
    } catch (error) {
      return null;
    }
  },

  // Get count of records in table
  async getRecordCount() {
    try {
      const {count, error} = await supabase
        .from('bookings_patient_data_bp2025')
        .select('*', {count: 'exact', head: true});

      if (error) {
        return {success: false, error};
      }

      return {success: true, count};
    } catch (error) {
      return {success: false, error};
    }
  }
};